/**
 * Firebase-backed billing service.
 *
 * A thin client over two Cloud Functions callables. It holds no Stripe key and
 * makes no pricing decision: it asks the server for a hosted Checkout or Customer
 * Portal URL and sends the browser there. Entitlements never come back through
 * here — they arrive only when the Stripe webhook processes the resulting
 * subscription (§13), so returning to the app grants nothing on its own.
 */

import { getFunctions, httpsCallable } from "firebase/functions";
import { REGION } from "../../config/regions.js";

/**
 * Turn a callable rejection into a message safe to show a user.
 *
 * @param {unknown} error
 * @returns {Error}
 */
function toUserError(error) {
  const message =
    error && typeof error === "object" && "message" in error && typeof error.message === "string"
      ? error.message
      : "";
  const cleaned = message.replace(/^[a-z-]+:\s*/i, "").trim();
  return new Error(
    cleaned.length > 0 ? cleaned : "We could not reach KinCode. Check your connection and retry.",
  );
}

/**
 * @param {import("./app.js").FirebaseContext} context
 * @returns {import("../interfaces.js").BillingService}
 */
export function createFirebaseBillingService(context) {
  const functions = getFunctions(context.app, REGION);

  /**
   * @template T
   * @param {string} name
   * @returns {(payload?: unknown) => Promise<T>}
   */
  function callable(name) {
    const fn = httpsCallable(functions, name);
    return async (payload = {}) => {
      try {
        const result = await fn(payload);
        return /** @type {T} */ (result.data);
      } catch (error) {
        throw toUserError(error);
      }
    };
  }

  const callCheckout = callable("createStripeCheckoutSession");
  const callPortal = callable("createStripePortalSession");

  return {
    async startCheckout(cadence) {
      const { url } = /** @type {{ url: string }} */ (await callCheckout({ cadence }));
      window.location.assign(url);
    },

    async openPortal() {
      const { url } = /** @type {{ url: string }} */ (await callPortal());
      window.location.assign(url);
    },
  };
}
